import { useEffect, useState } from "react";
import axios from "axios";
import useReveal from "../../hooks/useReveal";
import { ArrowRight, ShieldCheck, Heartbeat } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function About() {
  const [data, setData] = useState(null);
  const { ref, visible, revealKey } = useReveal();

  useEffect(() => {
    axios.get(`${API}/public/pages/home.about`)
      .then((r) => setData(r.data?.value || null))
      .catch(() => {});
  }, []);

  const heading = data?.heading || "Pioneering pharmaceutical solutions since day one";
  const body = data?.body || "We design, manufacture and deliver medicines that meet the highest standards of safety and efficacy — partnering with clinicians, hospitals and distributors across India and beyond.";
  const image = data?.image_url ? `${BACKEND}${data.image_url}` : "";

  const stableKey = revealKey;

  return (
    <section ref={ref} className="relative py-20 lg:py-32 bg-white" data-testid="home-about" id="about">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className={`lg:col-span-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#C2410C]/10 rounded-2xl -z-10" aria-hidden />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#0EA5E9]/10 rounded-2xl -z-10" aria-hidden />
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-[#F1F5F9]">
                {image ? (
                  <img
                    key={`about-img-${stableKey}`}
                    src={image}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    style={{ animation: visible ? "revealLeftRight 1.3s cubic-bezier(0.65,0,0.35,1) both, kenBurns 12s ease-out 1.3s both" : "none" }}
                  />
                ) : (
                  <div
                    key={`about-img-${stableKey}`}
                    aria-hidden
                    className="w-full h-full"
                    style={{
                      background:
                        "radial-gradient(120% 100% at 0% 0%, #C2410C22 0%, transparent 55%), radial-gradient(120% 100% at 100% 100%, #0EA5E933 0%, transparent 55%), linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)",
                      animation: visible ? "revealLeftRight 1.3s cubic-bezier(0.65,0,0.35,1) both, kenBurns 12s ease-out 1.3s both" : "none",
                    }}
                  />
                )}
              </div>
              <div
                key={`about-card-${stableKey}`}
                className="absolute -bottom-8 -right-4 sm:-right-8 bg-white shadow-xl rounded-2xl p-5 max-w-[220px] border border-[#E2E8F0]"
                style={{ animation: visible ? "blurUp 0.7s ease-out 1.1s both" : "none" }}
                data-testid="about-floating-card"
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#C2410C] font-semibold">Trusted by</div>
                <div className="mt-1 text-3xl font-bold text-[#1F2937]" style={{ fontFamily: "'Manrope', sans-serif" }}>500+</div>
                <div className="text-xs text-[#4B5563]">healthcare partners</div>
              </div>
            </div>
          </div>

          <div className={`lg:col-span-6 transition-all duration-700 delay-150 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div
              key={`about-badge-${stableKey}`}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0EA5E9]/10 text-[#0369A1] text-xs tracking-[0.2em] uppercase font-semibold"
              style={{ animation: visible ? "blurUp 0.6s ease-out both" : "none" }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#0EA5E9]" /> About us
            </div>
            <h2
              key={`about-h2-${stableKey}`}
              className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1F2937] leading-[1.1]"
              style={{ fontFamily: "'Manrope', sans-serif", animation: visible ? "blurUp 0.8s ease-out 0.15s both" : "none" }}
            >
              {heading}
            </h2>
            <p
              key={`about-p-${stableKey}`}
              className="mt-6 text-base sm:text-lg text-[#4B5563] leading-relaxed whitespace-pre-line"
              style={{ animation: visible ? "blurUp 0.8s ease-out 0.3s both" : "none" }}
            >
              {body}
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { icon: ShieldCheck, tag: "Quality first", text: "ISO-certified manufacturing with rigorous QA at every step." },
                { icon: Heartbeat, tag: "Patient-led", text: "Affordable, accessible medicines designed for real-world use." },
              ].map((it, i) => (
                <div
                  key={`about-card-${i}-${stableKey}`}
                  className="bg-[#F8FAFC] rounded-xl p-5 border border-[#E2E8F0] hover:border-[#C2410C] hover:shadow-sm transition-all duration-300"
                  style={{ animation: visible ? `blurUp 0.7s ease-out ${0.45 + i * 0.12}s both` : "none" }}
                >
                  <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#C2410C]">
                    <it.icon size={14} weight="fill" /> {it.tag}
                  </div>
                  <p className="mt-2 text-sm text-[#374151] leading-relaxed">{it.text}</p>
                </div>
              ))}
            </div>
            <a
              key={`about-cta-${stableKey}`}
              href="/about"
              className="mt-10 inline-flex items-center gap-2 text-[#C2410C] hover:text-[#9A3412] font-semibold text-sm group"
              style={{ animation: visible ? "blurUp 0.7s ease-out 0.75s both" : "none" }}
            >
              Learn more about us
              <ArrowRight size={16} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
